import * as Sentry from '@sentry/vue';
import posthog from 'posthog-js';

/**
 * Log an unhandled / unexpected error.
 *
 * Always writes to `console.error`, and forwards to Sentry and PostHog:
 *   - if any arg is an `Error`, capture it as an exception (Sentry gets the
 *     full arg list as extra context);
 *   - otherwise capture the first arg stringified as a message at "error"
 *     level on Sentry, and wrapped in `new Error(...)` for PostHog (which
 *     only ingests `Error` instances).
 *
 * Both SDKs are initialized on every environment (local / test / production)
 * and events are tagged with `environment` so they can be filtered in each
 * tool's UI — see [src/main.ts](src/main.ts) and [src/data/env.ts](src/data/env.ts).
 *
 * Signature mirrors `console.error`:
 *
 * ```ts
 * try { ... } catch (err) { logError('Failed to load invoice', err); }
 * ```
 */
export function logError(...args: unknown[]): void {
  console.error(...args);

  const err = args.find((a) => a instanceof Error);
  if (err instanceof Error) {
    Sentry.captureException(err, { extra: { args } });
    posthog.captureException(err);
  } else if (args.length > 0) {
    Sentry.captureMessage(String(args[0]), 'error');
    posthog.captureException(new Error(String(args[0])));
  }
}
